import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const StockChart = () => {
  const { user, loading: authLoading } = useAuth();
  const [symbols, setSymbols] = useState([]);
  const [selectedSymbol, setSelectedSymbol] = useState('');
  const [interval, setInterval] = useState('1d');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Initialize dates (1 year back from today)
  useEffect(() => {
    const today = new Date();
    const oneYearAgo = new Date(today);
    oneYearAgo.setFullYear(today.getFullYear() - 1);
    
    setEndDate(today.toISOString().split('T')[0]);
    setStartDate(oneYearAgo.toISOString().split('T')[0]);
  }, []);

  // Load available stock symbols
  useEffect(() => {
    const loadSymbols = async () => {
      if (authLoading) {
        console.log('Auth still loading, waiting...');
        return;
      }

      if (!user) {
        console.log('No user found, should be redirected to login');
        setError('Please log in to view stock charts');
        return;
      }

      try {
        const token = localStorage.getItem('token');
        console.log('User:', user);
        console.log('Token present:', !!token);
        
        if (!token) {
          setError('Authentication token not found. Please log in again.');
          return;
        }

        console.log('Loading symbols...');
        setLoading(true);
        
        const response = await axios.get('/historical/symbols', {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        console.log('Symbols loaded:', response.data.symbols?.length || 0);
        setSymbols(response.data.symbols || []);
        setError(''); // Clear any previous errors
      } catch (error) {
        console.error('Error loading symbols:', error);
        console.error('Error response:', error.response?.data);
        
        if (error.response?.status === 401) {
          setError('Authentication failed. Please log in again.');
          // Clear invalid token
          localStorage.removeItem('token');
        } else {
          setError(`Error loading stock symbols: ${error.response?.data?.message || error.message}`);
        }
      } finally {
        setLoading(false);
      }
    };

    loadSymbols();
  }, [user, authLoading]);

  // Load chart data
  const loadChart = async () => {
    if (!selectedSymbol) {
      setError('Please select a stock symbol');
      return;
    }

    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Please log in to view stock charts');
        return;
      }

      const params = new URLSearchParams({
        interval,
        startDate,
        endDate
      });

      console.log('Loading chart for:', selectedSymbol, 'with params:', Object.fromEntries(params));

      const response = await axios.get(`/historical/chart/${selectedSymbol}?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Chart response:', response.data);
      setChartData(response.data);
      displayChart(response.data.data, selectedSymbol);
    } catch (error) {
      console.error('Error loading chart:', error);
      console.error('Error response:', error.response?.data);
      
      if (error.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
      } else if (error.response?.status === 404) {
        setError('No data found for this stock symbol and date range.');
      } else {
        setError(`Error loading chart: ${error.response?.data?.message || error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Display chart using Plotly
  const displayChart = (data, symbol) => {
    if (!window.Plotly) {
      setError('Chart library not loaded');
      return;
    }

    const trace = {
      x: data.map(d => d.date),
      close: data.map(d => d.close),
      decreasing: { line: { color: '#FF4444' } },
      high: data.map(d => d.high),
      increasing: { line: { color: '#00AA00' } },
      line: { color: 'rgba(31,119,180,1)' },
      low: data.map(d => d.low),
      open: data.map(d => d.open),
      type: 'candlestick',
      xaxis: 'x',
      yaxis: 'y'
    };

    const layout = {
      title: `${symbol} Stock Price`,
      dragmode: 'zoom',
      margin: {
        r: 10,
        t: 50,
        b: 40,
        l: 60
      },
      showlegend: false,
      xaxis: {
        autorange: true,
        domain: [0, 1],
        range: [data[0].date, data[data.length - 1].date],
        rangeslider: { range: [data[0].date, data[data.length - 1].date] },
        title: 'Date',
        type: 'date'
      },
      yaxis: {
        autorange: true,
        domain: [0, 1],
        range: [Math.min(...data.map(d => d.low)) * 0.95, Math.max(...data.map(d => d.high)) * 1.05],
        title: 'Price ($)',
        type: 'linear'
      }
    };

    const config = {
      responsive: true,
      displayModeBar: true,
      modeBarButtonsToRemove: ['pan2d', 'lasso2d']
    };

    window.Plotly.newPlot('chartContainer', [trace], layout, config);
  };

  // Show loading state while authentication is being checked
  if (authLoading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show login prompt if user is not authenticated
  if (!user) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Authentication Required</h1>
            <p className="text-gray-600 mb-6">Please log in to access the stock charts.</p>
            <a 
              href="/login" 
              className="inline-block bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors"
            >
              Go to Login
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Stock Chart Viewer</h1>
          <p className="text-gray-600">Select a stock symbol and time interval to view historical candlestick charts</p>
        </div>

        <div className="flex flex-wrap gap-4 mb-8 justify-center items-end">
          <div className="flex flex-col">
            <label htmlFor="stockSymbol" className="block text-sm font-medium text-gray-700 mb-1">
              Stock Symbol:
            </label>
            <select
              id="stockSymbol"
              value={selectedSymbol}
              onChange={(e) => setSelectedSymbol(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={loading}
            >
              <option value="">
                {loading ? 'Loading symbols...' : symbols.length === 0 ? 'No symbols available' : 'Select a stock...'}
              </option>
              {symbols.map((stock) => (
                <option key={stock.symbol} value={stock.symbol}>
                  {stock.symbol} ({stock.dataPoints} data points)
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label htmlFor="interval" className="block text-sm font-medium text-gray-700 mb-1">
              Time Interval:
            </label>
            <select
              id="interval"
              value={interval}
              onChange={(e) => setInterval(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="1d">1 Day</option>
              <option value="1w">1 Week</option>
              <option value="1m">1 Month</option>
              <option value="3m">3 Months</option>
              <option value="6m">6 Months</option>
              <option value="1y">1 Year</option>
              <option value="2y">2 Years</option>
              <option value="5y">5 Years</option>
            </select>
          </div>

          <div className="flex flex-col">
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
              Start Date:
            </label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="flex flex-col">
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
              End Date:
            </label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="flex flex-col">
            <label className="block text-sm font-medium text-gray-700 mb-1">&nbsp;</label>
            <button
              onClick={loadChart}
              disabled={loading}
              className={`px-6 py-2 rounded-md font-medium ${
                loading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
              } text-white transition-colors`}
            >
              {loading ? 'Loading...' : 'Load Chart'}
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Debug Information */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-md">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Debug Info:</h3>
            <div className="text-xs text-gray-600 space-y-1">
              <p>User: {user ? `${user.firstName} ${user.lastName} (${user.email})` : 'Not logged in'}</p>
              <p>Token: {localStorage.getItem('token') ? 'Present' : 'Missing'}</p>
              <p>Symbols loaded: {symbols.length}</p>
              <p>Loading: {loading ? 'Yes' : 'No'}</p>
            </div>
          </div>
        )}

        {chartData && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">Chart Statistics</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p><strong>Data Points:</strong> {chartData.stats.totalDataPoints}</p>
                <p><strong>Date Range:</strong> {chartData.stats.dateRange.start} to {chartData.stats.dateRange.end}</p>
              </div>
              <div>
                <p><strong>Price Range:</strong> ${chartData.stats.priceRange.min.toFixed(2)} - ${chartData.stats.priceRange.max.toFixed(2)}</p>
              </div>
              <div>
                <p><strong>Avg Volume:</strong> {chartData.stats.volumeRange.avg.toLocaleString()}</p>
              </div>
            </div>
          </div>
        )}

        <div
          id="chartContainer"
          className="w-full h-96 border border-gray-300 rounded-md"
          style={{ minHeight: '600px' }}
        ></div>
      </div>
    </div>
  );
};

export default StockChart;
